import React, { useState, useRef, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import Typography from "@material-ui/core/Typography";
import { withStyles } from "@material-ui/core/styles";
import { object, string, boolean } from "yup";
import { Formik } from "formik";
import { authorizedRequest } from "utils/api";
import { ToastError, ToastSuccess } from "components/Global/Toasts/Toasty";
import { getRecipientsConfig, updateRecipientsConfig, updateShareRecords, createShareRecords } from "./lenderReportingDashboard.store";
import { connect } from "react-redux";
import { compose } from "redux";
import ShareReportForm from "./shareReportForm";
import ShareEmailTemplate from "./shareEmailTemplate";
import ShareHistoryModal from "./shareHistoryModal";
import { trackLCReportShare } from "lib/mixpanel";
import { v4 as uuidv4 } from "uuid";

const styles = () => ({
  dialogTitle: {
    fontFamily: "work sans",
    fontSize: "20px",
    marginTop: "25px",
    marginLeft: "25px",
    fontWeight: "500"
  }
});

const generatePinOfLength = length => Array(length).fill(0).map(() => Math.floor(Math.random() * 10)).join("");

const initialValues = {
  email: "",
  saveRecipients: true,
  subject: "",
  message: ""
};
// regex for list of comma separated emails
const formatRegex = /^(([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)(\s*,\s*|\s*$))*$/u;
const emailDelimiterRegex = /([;])|((,\s*)$)/u;

const validationSchema = object().shape({
  email: string().required("REQUIRED").test("delimiter", "INVALID EMAIL DELIMITER", value =>
    !(emailDelimiterRegex.test(value))).matches(formatRegex, "INVALID EMAIL"),
  saveRecipients: boolean(),
  subject: string().required("REQUIRED"),
  message: string()
});

/* NOSONAR javascript:S1117 */
const ShareReportModal = ({ openShareModal, setOpenShareModal, taskDetails, userEmail, rgiId, userName, company, expirationDate, authentication,
  prevPin, submitReport, getRecipientsConfig, recipientsConfig, updateRecipientsConfig, shareHistory, setShareHistorySaved, displayShareHistoryScreen,
  setDisplayShareHistoryScreen, displayShareReportScreen, setDisplayShareReportScreen, createShareRecords, classes, setAsMarkedEmail, setShareHistory, updateShareRecords }) => {

  const staticElement = useRef();
  const pin = !prevPin ? generatePinOfLength(6) : prevPin;
  const [formState, setFormState] = useState(initialValues);
  const [emailsList, setEmailsList] = useState("");
  const loanId = taskDetails?.tasks[0]?.loanId;

  useEffect(() => {
    if(loanId && company) {
      getRecipientsConfig({ loanId: taskDetails.tasks[0].loanId, companyId: company.id });
    }
  }, [taskDetails]);

  useEffect(() => {
      setEmailsList(recipientsConfig?.emailsList || "");
      return () => {
        setEmailsList("");
      };
  }, [recipientsConfig]);

  const sendEmail = () => {
    const requests = [];
    const selectedContacts = formState.email;
    const emailSessionId = uuidv4();
    selectedContacts.forEach(email => {
      const body = {
        emailAuth: { pin, rgiId, expirationDate },
        email: {
          recipient: email,
          subject: formState.subject,
          replyTo: userEmail,
          tag: rgiId,
          htmlBody: formState.message + staticElement.current.innerHTML,
          metadata: { sessionId: emailSessionId }
        }
      };
      requests.push(
        authorizedRequest(`/lrd/email/${rgiId}`, "POST", body, null, false)
      );
    });

     return Promise.all(requests).then((res) => {
        res.forEach(record => {
          if (!record.emailSend || record.emailSend.ErrorCode !== 0) {
          ToastError(`Email could not be sent to ${record.emailSend.To}`);
          }
        });      
        // send the emails to save share history records
        createShareRecords({ contactId: authentication.user.contact.id, rgiId, emails: selectedContacts, metadata:{sessionId: emailSessionId}});
        setShareHistorySaved(true);
        ToastSuccess("Email successfully sent");
        submitReport();
        setAsMarkedEmail();
      }).catch(() => {
        ToastError("Email could not be sent");
      }).finally(() => {
       setOpenShareModal(false);
      });
  };

  const handleSubmit = (
    values, { setSubmitting }
  ) => {
    const splitEmails = values.email.split(",");
    const emails = [];
    splitEmails.forEach(email => emails.push(email.trim()));
    if(values.saveRecipients) {
      updateRecipientsConfig({ loanId, companyId: company.id, emailsList: values.email });
    }
    setFormState({
      email: emails,
      saveRecipients: values.saveRecipients,
      subject: values.subject,
      message: values.message
    });
    setSubmitting(false);
    setDisplayShareReportScreen(false);
    trackLCReportShare();
  };

  useEffect(() => {
    if(formState.email.length !== 0 && formState.subject.length !== 0 ) {
      sendEmail();
    }
  }, [formState]);

  const handleClose = () => {
    setOpenShareModal(false);
    setDisplayShareHistoryScreen(false);
    setDisplayShareReportScreen(false);
  };

  return (
    <div style={{ position: "absolute" }}>
      <Dialog open={openShareModal} onClose={handleClose} aria-labelledby="form-dialog-title" PaperProps={{ style: { borderRadius: "8px", maxWidth: "800px" } }}>
        {displayShareReportScreen && (
          <>
            <Typography className={classes.dialogTitle}>Share this report</Typography>
            <Formik
              render={props =>
                <ShareReportForm
                  taskDetails={taskDetails}
                  setOpenShareModal={setOpenShareModal}
                  emailsList={emailsList}
                  setDisplayShareReportScreen={setDisplayShareReportScreen}
                  {...props}
                />}
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              validateOnChange={false}
            /> 
          </>
        )}
        {displayShareHistoryScreen && (
          <ShareHistoryModal 
            shareHistory={shareHistory}
            setShareHistory={setShareHistory}
            updateShareRecords={updateShareRecords}
            rgiId={rgiId} />
        )}
      </Dialog>
      <ShareEmailTemplate
        staticElement={staticElement}
        userName={userName}
        company={company}
        taskDetails={taskDetails}
        rgiId={rgiId}
        expirationDate={expirationDate}
        pin={pin}
      />
    </div>
  );
};

const mapStateToProps = ({ lenderReportingDashboard, authentication }) => ({
  ...lenderReportingDashboard,
  authentication
});

export default compose(
  withStyles(styles),
  connect(mapStateToProps, {
    getRecipientsConfig,
    updateRecipientsConfig,
    updateShareRecords,
    createShareRecords
  })
)(ShareReportModal);
